import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Body1, FluentProvider, Spinner } from '@fluentui/react-components'
import { appTheme } from './lib/theme'
import { bootstrap } from './api/client'
import App from './App.tsx'
import { PdfPane } from './components/PdfPane'
import { getInvoice, getUpload } from './lib/data'
import { loadSession, canPreviewPdf } from './lib/identity'
import type { Upload } from './lib/types'
import { errorMessage } from './api/errors'

const root = createRoot(document.getElementById('root')!)

// Full-screen document view (item 2, C7, 2026-08-13). There is no router in
// this app (see app/README.md and CLAUDE.md's `NavValue` string-union note) —
// so the "Full screen" button in PdfPane opens THIS SAME origin with a
// `?doc=<invoiceId>` query parameter, and this branch, checked before the
// normal app shell mounts, recognizes it and renders the pane full-viewport
// instead. Same component, same page-span restriction as the modal, fed by
// its own round trip to `getInvoice`/`getUpload` — the new tab never receives
// the signed upload URL from its opener, it asks the API for its own.
function docIdFromLocation(): string | null {
  return new URLSearchParams(window.location.search).get('doc')
}

type DocState =
  | { status: 'loading' }
  | { status: 'ready'; upload: Upload | undefined; pageStart?: number; pageEnd?: number }
  | { status: 'error'; message: string }
  | { status: 'forbidden' }

// `export`ed for no consumer's sake — nothing imports main.tsx — but
// `react-refresh/only-export-components` flags an unexported component
// defined in a file with no exports at all ("move it to a separate file").
// This is the one component genuinely at home in the entry file rather than
// under `components/`: it exists only to be the thing main.tsx picks between
// before the app shell mounts, and it has no other caller.
export function StandaloneDocumentView({ invoiceId }: { invoiceId: string }) {
  const [state, setState] = useState<DocState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    // Load this tab's own session first — a `?doc=` tab opened without file
    // access should say so plainly, the same refusal `canPreviewPdf` gives
    // the modal, rather than falling through to `getInvoice`/`getUpload` and
    // rendering a generic "couldn't load" once the API 403s. This is a UX
    // fix, not a security one: the presigned-URL endpoint is still the actual
    // authority and enforces independently of anything checked here.
    loadSession()
      .then(async (session) => {
        if (cancelled) return
        if (!canPreviewPdf(session)) {
          setState({ status: 'forbidden' })
          return
        }
        const invoice = await getInvoice(invoiceId)
        const upload = invoice.upload_id ? await getUpload(invoice.upload_id) : undefined
        if (cancelled) return
        setState({
          status: 'ready',
          upload,
          pageStart: invoice.upload_page_start ?? undefined,
          pageEnd: invoice.upload_page_end ?? undefined,
        })
      })
      .catch((e) => {
        if (!cancelled) setState({ status: 'error', message: errorMessage(e) })
      })
    return () => {
      cancelled = true
    }
  }, [invoiceId])

  if (state.status === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner label="Loading document…" />
      </div>
    )
  }
  if (state.status === 'forbidden') {
    return (
      <div style={{ padding: 32, maxWidth: 640 }}>
        <Body1>You don't have permission to preview documents. Ask an administrator for document access.</Body1>
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div style={{ padding: 32, maxWidth: 640 }}>
        <Body1>Could not open this document: {state.message}</Body1>
      </div>
    )
  }
  return (
    <div style={{ height: '100vh', boxSizing: 'border-box', padding: 16 }}>
      <PdfPane upload={state.upload} pageStart={state.pageStart} pageEnd={state.pageEnd} />
    </div>
  )
}

// Read the runtime configuration and complete sign-in before the app renders —
// nothing in the shell has to cope with "not signed in yet". A failure here is
// terminal and says so: there is no usable read-only mode behind it.
root.render(
  <StrictMode>
    <FluentProvider theme={appTheme} style={{ minHeight: '100vh' }}>
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner label="Signing in…" />
      </div>
    </FluentProvider>
  </StrictMode>,
)

bootstrap().then(
  () => {
    const docId = docIdFromLocation()
    root.render(
      <StrictMode>
        <FluentProvider theme={appTheme} style={{ minHeight: '100vh' }}>
          {docId ? <StandaloneDocumentView invoiceId={docId} /> : <App />}
        </FluentProvider>
      </StrictMode>,
    )
  },
  (e: unknown) => {
    const message = e instanceof Error ? e.message : String(e)
    root.render(
      <StrictMode>
        <FluentProvider theme={appTheme} style={{ minHeight: '100vh' }}>
          <div style={{ padding: 32, maxWidth: 640 }}>
            <h2>Accounts Payable could not start</h2>
            <p>{message}</p>
            <p>Reload the page. If it keeps happening, tell the administrator what this said.</p>
          </div>
        </FluentProvider>
      </StrictMode>,
    )
  },
)
